import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";
import {
  getInternalMfaStatus,
  requestInternalMfaCode,
  verifyInternalMfaCode,
} from "@/lib/mfa.functions";
import { readSessionId, rememberMfaVerified } from "@/lib/mfa-session-cache";

/** Sitzungskennung des aktuellen Zugriffstokens (nur als Speicherschlüssel). */
async function currentSessionId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return readSessionId(data.session?.access_token ?? null);
}


const searchSchema = z.object({
  redirect: z
    .string()
    .startsWith("/")
    .default("/admin")
    .catch("/admin"),
});

export const Route = createFileRoute("/intern_/mfa")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Zwei-Faktor-Bestätigung – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ search }) => {
    const { supabase: sb } = await import("@/integrations/supabase/client");
    const { data, error } = await sb.auth.getUser();
    if (error || !data.user) {
      throw redirect({
        to: "/intern",
        search: { redirect: search.redirect },
      });
    }
    const email = (data.user.email ?? "").toLowerCase();
    if (!email.endsWith("@creategy.de")) {
      throw redirect({ to: "/cockpit" });
    }
  },
  component: MfaPage,
});

type Status = "idle" | "sending" | "sent" | "verifying" | "error";

function MfaPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const requestCode = useServerFn(requestInternalMfaCode);
  const verifyCode = useServerFn(verifyInternalMfaCode);
  const getStatus = useServerFn(getInternalMfaStatus);
  const [status, setStatus] = useState<Status>("idle");
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const requested = useRef(false);

  // Initial: Status prüfen. Ein Code wird NUR angefordert, wenn der Status
  // eindeutig "nötig und nicht bestätigt" liefert. Früher wurde bei einem
  // unklaren Status "sicherheitshalber" ein Code verschickt – das erzeugte
  // unnötige Mails und Wartesperren, obwohl die Sitzung bestätigt war.
  const checkStatus = async (options: { auto: boolean }) => {
    setError(null);
    setInfo(null);
    try {
      const st = await getStatus();
      if (!st.required || st.verified) {
        rememberMfaVerified(await currentSessionId());
        navigate({ to: search.redirect, replace: true });
        return;
      }
      if (options.auto) await sendNewCode(true);
      else setInfo("Für diese Sitzung ist eine Bestätigung nötig.");
    } catch {
      setStatus("error");
      setError(
        "Der Anmeldestatus konnte gerade nicht geprüft werden. Bitte erneut prüfen – ein Code wurde vorsichtshalber nicht verschickt.",
      );
    }
  };

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    void checkStatus({ auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendNewCode = async (initial: boolean) => {
    setError(null);
    setInfo(null);
    setStatus("sending");
    try {
      const res = await requestCode();
      if (!res.ok) {
        setStatus("error");
        setError(
          res.reason === "send_failed"
            ? `${res.message} Falls es erneut nicht klappt, wenden Sie sich bitte an eine Administratorin oder einen Administrator.`
            : res.message,
        );
        // Nach einem Versandfehler darf sofort erneut versucht werden.
        if (res.reason === "send_failed") setCooldown(0);
        return;
      }
      setStatus("sent");
      setInfo(
        initial
          ? "Wir haben Ihnen einen 6-stelligen Code per E-Mail geschickt."
          : "Ein neuer Code ist unterwegs.",
      );
      setCooldown(60);
    } catch (err) {
      setStatus("error");
      setError(
        err instanceof Error
          ? err.message
          : "Der Code konnte nicht gesendet werden. Bitte erneut versuchen.",
      );
    }
  };


  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError("Bitte den 6-stelligen Code eingeben.");
      return;
    }
    setError(null);
    setInfo(null);
    setStatus("verifying");
    try {
      const res = await verifyCode({ data: { code } });
      if (res.ok) {
        // Sitzung sofort als bestätigt merken – so prüft die Admin-Shell nicht
        // erneut, während der Eintrag serverseitig gerade geschrieben wurde.
        rememberMfaVerified(await currentSessionId());
        navigate({ to: search.redirect, replace: true });
        return;
      }

      setStatus("error");
      setError(res.message);
      if (res.reason === "attempts_exceeded" || res.reason === "expired" || res.reason === "no_code") {
        setCode("");
      }
    } catch (err) {
      setStatus("error");
      setError(
        err instanceof Error
          ? err.message
          : "Prüfung nicht möglich. Bitte erneut versuchen.",
      );
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/intern", replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6 md:px-12">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-extrabold">
            Zwei-Faktor-Bestätigung
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Zur Sicherheit haben wir Ihnen einen 6-stelligen Code an Ihre
            creategy-Adresse gesendet. Bitte tragen Sie ihn hier ein.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
            <label htmlFor="mfa-code" className="block text-sm font-semibold">
              Anmeldecode
            </label>
            <input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D+/g, "").slice(0, 6))
              }
              className="w-full rounded-md border border-border bg-background px-3 py-3 text-center font-display text-2xl tracking-[0.4em] outline-none focus:border-foreground"
              aria-label="6-stelliger Anmeldecode"
              required
            />

            {info && (
              <p role="status" className="text-xs text-muted-foreground">
                {info}
              </p>
            )}
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "sending" || status === "verifying" || code.length !== 6}
              className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {status === "verifying" ? "Wird geprüft…" : "Bestätigen"}
            </button>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-1 text-xs">
              <button
                type="button"
                onClick={() => void checkStatus({ auto: false })}
                className="text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Status erneut prüfen
              </button>

              <button
                type="button"
                disabled={cooldown > 0 || status === "sending"}
                onClick={() => sendNewCode(false)}
                className="text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-60"
              >
                {cooldown > 0
                  ? `Neuen Code senden in ${cooldown}s`
                  : status === "sending"
                    ? "Wird gesendet…"
                    : "Neuen Code senden"}
              </button>
              <button
                type="button"
                onClick={signOut}
                className="text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Abmelden
              </button>
            </div>
          </form>

          <p className="mt-8 text-xs text-muted-foreground">
            Der Code ist 10&nbsp;Minuten gültig. Nach 5 Fehlversuchen wird er
            verworfen und Sie müssen einen neuen Code anfordern.
          </p>
        </div>
      </main>
    </div>
  );
}
